import React, { useState } from "react";
import { APIURL } from "../globals.jsx";
import {
  Box,
  Drawer,
  Typography,
  Avatar,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  HomeOutlined,
  BusinessOutlined,
  Groups2Outlined,
  Search,
  ShoppingCartOutlined,
  PublicOutlined,
  AccountBalanceWalletOutlined,
  BadgeOutlined,
  DirectionsCarOutlined,
  HomeWorkOutlined,
  WorkOutlined,
  PersonOutlined,
  LocalShippingOutlined,
  LockOutlined,
  RestorePageOutlined,
  ExpandMore,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "state/api";
import { useDispatch } from "react-redux";

// ── nav structure ──────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    group: null,
    items: [
      { text: "Dashboard", icon: <HomeOutlined sx={{ fontSize: 17 }} />, route: "/dashboard" },
    ],
  },
  {
    group: "RECORDS",
    items: [
      { text: "Entities", icon: <BusinessOutlined sx={{ fontSize: 17 }} />, route: "/entities" },
      { text: "Expired Entities", icon: <BusinessOutlined sx={{ fontSize: 17 }} />, route: "/expiredentities" },
      { text: "Customers", icon: <Groups2Outlined sx={{ fontSize: 17 }} />, route: "/customers" },
      { text: "Expired Customers", icon: <Groups2Outlined sx={{ fontSize: 17 }} />, route: "/expiredcustomer" },
    ],
  },
  {
    group: "TRANSACTIONS",
    items: [
      { text: "Cash DPMSR", icon: <ShoppingCartOutlined sx={{ fontSize: 17 }} />, route: "/dpmsr" },
    ],
  },
  {
    group: "SCREENING & LOGS",
    items: [
      { text: "Instant Screen", icon: <Search sx={{ fontSize: 17 }} />, route: "/search" },
      { text: "Screening Log", icon: <RestorePageOutlined sx={{ fontSize: 17 }} />, route: "/screeninglog" },
    ],
  },
  {
    group: "RBA DEFINITIONS",
    items: [
      { text: "Countries Risk", icon: <PublicOutlined sx={{ fontSize: 17 }} />, route: "/countriesrisk" },
      { text: "Source Of Funds", icon: <AccountBalanceWalletOutlined sx={{ fontSize: 17 }} />, route: "/fundsource" },
      { text: "ID Type", icon: <BadgeOutlined sx={{ fontSize: 17 }} />, route: "/idtype" },
      { text: "Lic Type", icon: <DirectionsCarOutlined sx={{ fontSize: 17 }} />, route: "/lictype" },
      { text: "Residential Status", icon: <HomeWorkOutlined sx={{ fontSize: 17 }} />, route: "/restype" },
      { text: "Business Activity", icon: <WorkOutlined sx={{ fontSize: 17 }} />, route: "/businessactivity" },
      { text: "PEP Risk", icon: <PersonOutlined sx={{ fontSize: 17 }} />, route: "/peprisk" },
      { text: "Delivery Channel", icon: <LocalShippingOutlined sx={{ fontSize: 17 }} />, route: "/deliverychannel" },
    ],
  },
  {
    group: "MANAGEMENT",
    items: [
      { text: "Password Change", icon: <LockOutlined sx={{ fontSize: 17 }} />, route: null },
    ],
  },
];

// ── shared nav item component ──────────────────────────────────────────────────
const NavItem = ({ item, pathname, onClick }) => {
  const isActive = item.route && pathname.startsWith(item.route);
  return (
    <Box
      onClick={() => onClick(item.text)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: isActive ? "13px" : 2,
        py: 1.1,
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 500,
        fontFamily: "IBM Plex Sans, sans-serif",
        transition: "all 0.15s",
        color: isActive ? "#D97706" : "#64748B",
        background: isActive ? "rgba(217,119,6,.08)" : "transparent",
        borderLeft: isActive ? "3px solid #D97706" : "3px solid transparent",
        "&:hover": {
          background: "#F8FAFC",
          color: "#0F172A",
        },
      }}
    >
      <Box sx={{ display: "flex", flexShrink: 0, color: "inherit" }}>
        {item.icon}
      </Box>
      {item.text}
    </Box>
  );
};

// ── group section ──────────────────────────────────────────────────────────────
const NavGroup = ({ group, items, pathname, onItemClick, open, onToggle }) => (
  <Box>
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        pt: 2.5,
        pb: 0.5,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: "#94A3B8",
          letterSpacing: "2px",
          textTransform: "uppercase",
          fontFamily: "IBM Plex Sans, sans-serif",
        }}
      >
        {group}
      </Typography>
      <ExpandMore
        sx={{
          fontSize: 16,
          color: "#94A3B8",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}
      />
    </Box>
    <Box
      sx={{
        maxHeight: open ? "500px" : "0px",
        overflow: "hidden",
        transition: "max-height 0.3s ease",
      }}
    >
      {items.map((item) => (
        <NavItem key={item.text} item={item} pathname={pathname} onClick={onItemClick} />
      ))}
    </Box>
  </Box>
);

// ── sidebar content ────────────────────────────────────────────────────────────
const SidebarContent = ({ user, pathname, onItemClick }) => {
  const initialOpen = NAV_GROUPS.reduce((acc, g) => {
    if (g.group) acc[g.group] = true;
    return acc;
  }, {});
  const [openGroups, setOpenGroups] = useState(initialOpen);

  const toggleGroup = (group) =>
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));

  const firstLetter = user?.name ? user.name.charAt(0).toUpperCase() : "U";

  return (
    <Box
      sx={{
        width: 260,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#fff",
        borderRight: "1px solid #E2E8F0",
      }}
    >
      {/* Logo */}
      <Box sx={{ px: 2.5, py: 2.5, borderBottom: "1px solid #E2E8F0" }}>
        <Box
          component="img"
          src="/logo.png"
          alt="AMLDiligence"
          sx={{ height: 34, display: "block" }}
        />
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {NAV_GROUPS.map((section) =>
          section.group === null ? (
            <Box key="standalone" sx={{ px: 1, pb: 0.5 }}>
              {section.items.map((item) => (
                <NavItem
                  key={item.text}
                  item={item}
                  pathname={pathname}
                  onClick={onItemClick}
                />
              ))}
            </Box>
          ) : (
            <Box key={section.group} sx={{ px: 1 }}>
              <NavGroup
                group={section.group}
                items={section.items}
                pathname={pathname}
                onItemClick={onItemClick}
                open={openGroups[section.group]}
                onToggle={() => toggleGroup(section.group)}
              />
            </Box>
          )
        )}
      </Box>

      {/* User info */}
      <Box
        sx={{
          borderTop: "1px solid #E2E8F0",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            background: "linear-gradient(135deg,#D97706,#F59E0B)",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "IBM Plex Sans, sans-serif",
          }}
        >
          {firstLetter}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              fontFamily: "IBM Plex Sans, sans-serif",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.name || ""}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: "#94A3B8",
              fontFamily: "IBM Plex Sans, sans-serif",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.email || ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── main export ────────────────────────────────────────────────────────────────
const SideBar = ({ user, isSidebarOpen, setIsSidebarOpen }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleClick = (text) => {
    let lcText = text.toLowerCase();

    if (lcText === "password change") {
      if (!user?.email) return;
      fetch(`${APIURL}/general/forgot-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      })
        .then(() => {
          setSnackbar({
            open: true,
            message: `A password reset link has been sent to ${user.email}. Please check your inbox and follow the instructions to set a new password.`,
            severity: "success",
          });
        })
        .catch(() => {
          setSnackbar({ open: true, message: "Failed to send reset link. Please try again.", severity: "error" });
        });
      return;
    }

    if (lcText === "instant screen") {
      lcText = "search";
    }

    dispatch(api.util.invalidateTags(["Dashboard", "Logs"]));
    navigate(`/${lcText.replace(/\s+/g, "")}`);
    // close drawer on mobile after navigation
    setIsSidebarOpen(false);
  };

  return (
    <>
      {/* Desktop: sticky sidebar */}
      <Box
        sx={{
          display: { xs: "none", lg: "block" },
          position: "sticky",
          top: 0,
          height: "100vh",
          flexShrink: 0,
          overflowY: "auto",
        }}
      >
        <SidebarContent user={user} pathname={pathname} onItemClick={handleClick} />
      </Box>

      {/* Mobile: drawer */}
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            border: "none",
          },
        }}
      >
        <SidebarContent user={user} pathname={pathname} onItemClick={handleClick} />
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SideBar;
